import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/apiInterceptor';
import {
  Box, Paper, Tabs, Tab, Grid,
  Stack, Typography, Chip, LinearProgress, Alert, AlertTitle,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

/* ===================== 颜色方案（深冷青） ===================== */
const C = {
  bg:       '#080c14',
  surface:   '#0d1220',
  border:    'rgba(255,255,255,0.06)',
  text:      'rgba(255,255,255,0.88)',
  textSec:   'rgba(255,255,255,0.42)',
  textDim:   'rgba(255,255,255,0.22)',
  accent:    '#00d4b8',    // 主色：冷青
  accentDim: 'rgba(0,212,184,0.12)',
  accentBor: 'rgba(0,212,184,0.28)',
  amber:     '#fbbf24',    // 转折
  amberDim:  'rgba(251,191,36,0.12)',
  amberBor:  'rgba(251,191,36,0.28)',
  red:       '#f87171',    // 冲突
  redDim:    'rgba(248,113,113,0.12)',
  redBor:    'rgba(248,113,113,0.28)',
  purple:    '#c084fc',    // 秘密
  purpleDim: 'rgba(192,132,252,0.12)',
  purpleBor: 'rgba(192,132,252,0.28)',
};

const getImportanceStyle = (importance) => {
  switch (importance) {
    case 'primary':   return { label: '主要', color: C.accent,   bg: C.accentDim,  border: C.accentBor  };
    case 'secondary': return { label: '次要', color: '#4ade80', bg: 'rgba(74,222,128,0.12)', border: 'rgba(74,222,128,0.28)' };
    default:          return { label: '一般', color: C.textSec,  bg: 'rgba(255,255,255,0.06)', border: 'rgba(255,255,255,0.12)' };
  }
};

const getTrustColor = (level) => {
  if (level >= 8) return C.accent;
  if (level >= 6) return '#4ade80';
  if (level >= 4) return C.amber;
  if (level >= 2) return '#fb923c';
  return C.red;
};

/* ===================== 主组件 ===================== */
const RelationshipPlanViewer = ({ novelId }) => {
  const [relationshipPlan, setRelationshipPlan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRelationship, setSelectedRelationship] = useState(null);
  const [error, setError] = useState(null);
  const [detailTab, setDetailTab] = useState(0);
  const [structureGenerated, setStructureGenerated] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 960 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 960);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (novelId) fetchRelationshipPlan();
  }, [novelId]);

  const fetchRelationshipPlan = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/novels/${novelId}/relationship-plan`);
      if (response.data.success) {
        setRelationshipPlan(response.data.data || []);
        setStructureGenerated(response.data.isGenerated === true);
        if (response.data.data?.length > 0) {
          setSelectedRelationship(response.data.data[0]);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || '获取关系规划失败');
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    const total = relationshipPlan.length;
    const totalMilestones    = relationshipPlan.reduce((s, r) => s + (r.roadmap?.length || 0), 0);
    const totalTurningPoints = relationshipPlan.reduce((s, r) => s + (r.turningPoints?.length || 0), 0);
    const totalConflicts    = relationshipPlan.reduce((s, r) => s + (r.conflicts?.length || 0), 0);
    return { total, totalMilestones, totalTurningPoints, totalConflicts };
  }, [relationshipPlan]);

  if (loading) {
    return (
      <Box sx={{ bgcolor: C.bg, minHeight: 500, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <LinearProgress sx={{ width: 200, '& .MuiLinearProgress-bar': { bgcolor: C.accent }, bgcolor: C.accentDim }} />
        <Typography sx={{ color: C.textSec, fontSize: '0.82rem' }}>加载关系规划...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ bgcolor: C.bg, p: 2 }}>
        <Alert severity="error" sx={{ bgcolor: C.redDim, border: `1px solid ${C.redBor}`, '& .MuiAlert-icon': { color: C.red } }}>
          <AlertTitle sx={{ color: C.red }}>加载失败</AlertTitle>
          {error}
          <Box component="button" onClick={fetchRelationshipPlan}
            sx={{ color: C.red, cursor: 'pointer', border: 'none', bgcolor: 'transparent', fontSize: '0.875rem', fontWeight: 600, display: 'block', mt: 1 }}>
            点击重试
          </Box>
        </Alert>
      </Box>
    );
  }

  if (relationshipPlan.length === 0) {
    return (
      <Box sx={{ bgcolor: C.bg, p: { xs: 2, sm: 4 }, maxWidth: 640, mx: 'auto' }}>
        {structureGenerated ? (
          <Alert severity="info" sx={{ bgcolor: C.accentDim, border: `1px solid ${C.accentBor}`, '& .MuiAlert-icon': { color: C.accent }, '& .MuiAlertTitle-root': { color: C.accent } }}>
            <AlertTitle>暂无关系规划数据</AlertTitle>
            故事结构已生成，但未提取到人物关系规划数据。
          </Alert>
        ) : (
          <Alert severity="info" sx={{ bgcolor: C.accentDim, border: `1px solid ${C.accentBor}`, '& .MuiAlert-icon': { color: C.accent }, '& .MuiAlertTitle-root': { color: C.accent } }}>
            <AlertTitle>尚未生成人物关系规划</AlertTitle>
            请先在「故事结构」Tab 中生成故事结构，AI 将自动分析并规划角色关系发展路径。
          </Alert>
        )}
      </Box>
    );
  }

  /* ===================== PC 布局（md+） ===================== */
  if (!isMobile) {
    return <PCLayout
      relationshipPlan={relationshipPlan}
      selectedRelationship={selectedRelationship}
      setSelectedRelationship={setSelectedRelationship}
      detailTab={detailTab}
      setDetailTab={setDetailTab}
      stats={stats}
    />;
  }

  /* ===================== 移动端布局（xs/sm） ===================== */
  return (
    <Box sx={{ bgcolor: C.bg, width: '100%', minHeight: 'calc(100vh - 200px)', color: C.text, display: 'flex', flexDirection: 'column' }}>

      {/* 顶部关系对网格（横向 2 列） */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${C.border}` }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <PersonIcon sx={{ fontSize: 16, color: C.accent }} />
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: C.textSec }}>关系</Typography>
          <Chip label={`${stats.total}对`} size="small" sx={{
            height: 16, fontSize: '0.6rem', ml: 'auto',
            bgcolor: C.accentDim, color: C.accent, border: `1px solid ${C.accentBor}`,
          }} />
        </Stack>
        <Grid container spacing={0.75}>
          {relationshipPlan.map((rel) => {
          const isSelected = selectedRelationship?.id === rel.id;
            const imp = getImportanceStyle(rel.importance);
          return (
              <Grid item xs={6} key={rel.id}>
                <Box
                  onClick={() => { setSelectedRelationship(rel); setDetailTab(0); }}
                  sx={{
                    p: 1, borderRadius: 1,
                cursor: 'pointer',
                    border: `1px solid ${isSelected ? C.accentBor : C.border}`,
                    bgcolor: isSelected ? C.accentDim : 'transparent',
                    transition: 'all 0.15s',
                    '&:active': { bgcolor: C.accentDim },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <FavoriteIcon sx={{ fontSize: 12, color: isSelected ? C.accent : C.textDim, flexShrink: 0 }} />
                    <Typography sx={{
                      fontSize: '0.72rem', fontWeight: isSelected ? 700 : 500,
                      color: isSelected ? C.accent : C.textSec,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
                    }}>
                      {rel.pair?.length >= 2 ? `${rel.pair[0]}—${rel.pair[1]}` : rel.id}
                    </Typography>
                  </Stack>
                  <Chip label={imp.label} size="small"
                    sx={{ height: 14, fontSize: '0.58rem', color: imp.color, bgcolor: imp.bg, border: `1px solid ${imp.border}` }} />
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      {/* 移动端 Tab（底部固定） */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 10,
        bgcolor: C.surface, borderBottom: `1px solid ${C.border}`,
        borderTop: `1px solid ${C.border}`,
      }}>
        <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              color: C.textDim, minHeight: 48, fontSize: '0.68rem', py: 0,
              '&.Mui-selected': { color: C.accent },
            },
            '& .MuiTabs-indicator': { bgcolor: C.accent },
          }}
        >
          <Tab label="路线图" icon={<TimelineIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
          <Tab label="转折" icon={<TrendingUpIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
          <Tab label="冲突" icon={<SwapHorizIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
          <Tab label="秘密" icon={<LockIcon sx={{ fontSize: 15 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* 内容区 */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 1.5 }}>
        {selectedRelationship ? (
          <MobileContent rel={selectedRelationship} detailTab={detailTab} />
        ) : (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography sx={{ color: C.textDim, fontSize: '0.875rem' }}>点击上方关系卡片查看详情</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* ===================== 移动端内容（按 Tab 类型展示选中关系的全部内容） ===================== */
const MobileContent = ({ rel, detailTab }) => {
  const imp = getImportanceStyle(rel.importance);

  if (detailTab === 0) {
    /* 路线图 */
    const roadmap = rel.roadmap || [];
    return (
      <Box>
        {/* 关系头 */}
        <Box sx={{ mb: 2, pb: 1.5, borderBottom: `1px solid ${C.border}` }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <FavoriteIcon sx={{ fontSize: 18, color: C.accent }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', flex: 1 }}>
              {rel.pair?.length >= 2 ? `${rel.pair[0]} — ${rel.pair[1]}` : rel.id}
            </Typography>
            <Chip label={`${imp.label}关系`} size="small"
              sx={{ height: 18, fontSize: '0.62rem', color: imp.color, bgcolor: imp.bg, border: `1px solid ${imp.border}` }} />
          </Stack>
          {rel.relationshipType && (
            <Typography sx={{ mt: 0.5, pl: 3.5, color: C.textSec, fontSize: '0.72rem' }}>{rel.relationshipType}</Typography>
          )}
        </Box>

        {roadmap.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim, fontSize: '0.875rem' }}>暂无发展路线图</Typography></Box>
        ) : (
          <Stack spacing={0}>
            {roadmap.map((stage, idx) => (
              <Box key={idx} sx={{ position: 'relative', pb: idx < roadmap.length - 1 ? 2 : 0 }}>
                {/* 时间线竖线 */}
                {idx < roadmap.length - 1 && (
                  <Box sx={{ position: 'absolute', left: 11, top: 28, bottom: 0, width: 1, bgcolor: C.border }} />
                )}
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: C.accent, color: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', fontWeight: 700, flexShrink: 0, mt: 0.25 }}>
                    {idx + 1}
                  </Box>
                  <Box sx={{ flex: 1, pb: 1.5, borderBottom: idx < roadmap.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{stage.stage}</Typography>
                      {stage.chapterRange?.length === 2 && (
                        <Chip label={`${stage.chapterRange[0]}-${stage.chapterRange[1]}章`} size="small"
                          sx={{ height: 18, fontSize: '0.6rem', bgcolor: C.accentDim, color: C.accent }} />
                      )}
                    </Stack>

                          {stage.trustLevel && (
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
                        <TrustBar label="起始" value={stage.trustLevel.start} />
                        <Typography sx={{ color: C.textDim }}>→</Typography>
                        <TrustBar label="结束" value={stage.trustLevel.end} />
                      </Stack>
                    )}

                          {stage.emotionalTone && (
                      <Typography sx={{ color: C.textSec, fontSize: '0.75rem', mb: 0.5 }}>
                        情感：<Box component="span" sx={{ color: C.text }}>{stage.emotionalTone}</Box>
                      </Typography>
                    )}

                    {stage.keyMilestones?.length > 0 && (
                      <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                        {stage.keyMilestones.map((m, mi) => (
                          <Box key={mi} sx={{ pl: 1.5, borderLeft: `2px solid ${C.accentBor}` }}>
                            <Typography sx={{ fontSize: '0.78rem' }}>
                              <Box component="span" sx={{ color: C.accent, fontWeight: 700, mr: 0.5 }}>第{m.chapter}章</Box>
                              <Box component="span" sx={{ color: C.textSec }}>{m.event}</Box>
                            </Typography>
                            {m.impact && <Typography sx={{ color: C.textDim, fontSize: '0.7rem', pl: 1.5 }}>→ {m.impact}</Typography>}
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    );
  }

  if (detailTab === 1) {
    /* 转折点 */
    const tps = rel.turningPoints || [];
    return (
      <Box>
        <Box sx={{ mb: 2, pb: 1.5, borderBottom: `1px solid ${C.border}` }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TrendingUpIcon sx={{ fontSize: 18, color: C.amber }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>关键转折</Typography>
            <Typography sx={{ color: C.textDim, fontSize: '0.72rem', ml: 'auto' }}>{tps.length}个</Typography>
          </Stack>
        </Box>
        {tps.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim, fontSize: '0.875rem' }}>暂无关键转折数据</Typography></Box>
        ) : (
          <Stack spacing={1}>
            {tps.map((tp, idx) => (
              <Box key={idx} sx={{ p: 1.5, borderRadius: 1, border: `1px solid ${C.border}`, bgcolor: C.surface }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Chip label={`第${tp.chapter}章`} size="small"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: C.amberDim, color: C.amber, border: `1px solid ${C.amberBor}`, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: tp.impact ? 0.5 : 0 }}>{tp.event}</Typography>
                    {tp.impact && (
                      <Typography sx={{ color: C.textSec, fontSize: '0.75rem' }}>
                        <Box component="span" sx={{ color: C.amber, fontWeight: 600 }}>影响：</Box>{tp.impact}
                      </Typography>
                    )}
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    );
  }

  if (detailTab === 2) {
    /* 冲突 */
    const conflicts = rel.conflicts || [];
    return (
      <Box>
        <Box sx={{ mb: 2, pb: 1.5, borderBottom: `1px solid ${C.border}` }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <SwapHorizIcon sx={{ fontSize: 18, color: C.red }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>冲突与和解</Typography>
            <Typography sx={{ color: C.textDim, fontSize: '0.72rem', ml: 'auto' }}>{conflicts.length}个</Typography>
          </Stack>
        </Box>
        {conflicts.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim, fontSize: '0.875rem' }}>暂无冲突数据</Typography></Box>
        ) : (
          <Stack spacing={1}>
            {conflicts.map((c, idx) => (
              <Box key={idx} sx={{ p: 1.5, borderRadius: 1, border: `1px solid ${C.border}`, bgcolor: C.surface }}>
                <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.75 }}>
                  <Chip label={`第${c.chapter}章`} size="small"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: C.redDim, color: C.red, border: `1px solid ${C.redBor}`, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: C.red, mb: 0.25 }}>{c.type}</Typography>
                    <Typography sx={{ color: C.textSec, fontSize: '0.75rem' }}>{c.description}</Typography>
                  </Box>
                </Stack>
                {c.resolution && (
                  <Box sx={{ pl: 0, pt: 0.75, borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#4ade80', flexShrink: 0 }}>解决</Typography>
                    <Typography sx={{ color: C.textSec, fontSize: '0.72rem', flex: 1 }}>{c.resolution.method}</Typography>
                    {c.resolution.chapter && (
                      <Chip label={`${c.resolution.chapter}章`} size="small" sx={{ height: 16, fontSize: '0.6rem', bgcolor: 'rgba(74,222,128,0.1)', color: '#4ade80' }} />
                    )}
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    );
  }

  if (detailTab === 3) {
    /* 秘密 */
    const secrets = rel.secretRevealSchedule || [];
    return (
      <Box>
        <Box sx={{ mb: 2, pb: 1.5, borderBottom: `1px solid ${C.border}` }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LockIcon sx={{ fontSize: 18, color: C.purple }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem' }}>秘密时间表</Typography>
            <Typography sx={{ color: C.textDim, fontSize: '0.72rem', ml: 'auto' }}>{secrets.length}个</Typography>
          </Stack>
        </Box>
        {secrets.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim, fontSize: '0.875rem' }}>暂无秘密透露数据</Typography></Box>
        ) : (
          <Stack spacing={1}>
            {secrets.map((s, idx) => (
              <Box key={idx} sx={{ p: 1.5, borderRadius: 1, border: `1px solid ${C.border}`, bgcolor: C.surface }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Chip label={`第${s.chapter}章`} size="small"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: C.purpleDim, color: C.purple, border: `1px solid ${C.purpleBor}`, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', mb: s.detail ? 0.25 : 0 }}>{s.secret}</Typography>
                    {s.detail && <Typography sx={{ color: C.textSec, fontSize: '0.75rem' }}>{s.detail}</Typography>}
                  </Box>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    );
  }

  return null;
};

/* ===================== PC 布局（内部组件，避免 hook 条件调用）===================== */
const PCLayout = ({ relationshipPlan, selectedRelationship, setSelectedRelationship, detailTab, setDetailTab, stats }) => {
  const imp = getImportanceStyle(selectedRelationship?.importance || 'minor');
  const rel = selectedRelationship;

  return (
    <Box sx={{ bgcolor: C.bg, width: '100%', minHeight: 'calc(100vh - 200px)', color: C.text, display: 'flex' }}>
      {/* 左侧边栏 */}
      <Box sx={{ width: 240, flexShrink: 0, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', bgcolor: C.surface }}>
        <Box sx={{ p: 2, borderBottom: `1px solid ${C.border}` }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <PersonIcon sx={{ fontSize: 18, color: C.accent }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>关系列表</Typography>
            <Chip label={`${stats.total}对`} size="small" sx={{ height: 18, fontSize: '0.62rem', ml: 'auto', bgcolor: C.accentDim, color: C.accent, border: `1px solid ${C.accentBor}` }} />
          </Stack>
          <Stack direction="row" spacing={1.5}>
            {[{ icon: <TimelineIcon sx={{ fontSize: 11 }} />, v: stats.totalMilestones, l: '阶段' },
             { icon: <TrendingUpIcon sx={{ fontSize: 11 }} />, v: stats.totalTurningPoints, l: '转折' },
             { icon: <SwapHorizIcon sx={{ fontSize: 11 }} />, v: stats.totalConflicts, l: '冲突' }].map(({ icon, v, l }) => (
              <Box key={l} sx={{ flex: 1, textAlign: 'center' }}>
                <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.3}>
                  <Box sx={{ color: C.textDim, display: 'flex' }}>{icon}</Box>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: C.text }}>{v}</Typography>
                </Stack>
                <Typography sx={{ fontSize: '0.62rem', color: C.textDim }}>{l}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {relationshipPlan.map((r) => {
            const isSelected = selectedRelationship?.id === r.id;
            const impS = getImportanceStyle(r.importance);
            return (
              <Box key={r.id} onClick={() => { setSelectedRelationship(r); setDetailTab(0); }}
                sx={{
                  px: 2, py: 1.25, borderBottom: `1px solid ${C.border}`,
                  cursor: 'pointer', transition: 'all 0.15s',
                  bgcolor: isSelected ? C.accentDim : 'transparent',
                  borderLeft: isSelected ? `3px solid ${C.accent}` : `3px solid transparent`,
                  '&:hover': { bgcolor: isSelected ? C.accentDim : 'rgba(255,255,255,0.02)' },
                }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: isSelected ? 700 : 400, color: isSelected ? C.accent : C.textSec, mb: 0.5, wordBreak: 'break-word' }}>
                  {r.pair?.length >= 2 ? `${r.pair[0]} — ${r.pair[1]}` : r.id}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Chip label={impS.label} size="small" sx={{ height: 16, fontSize: '0.58rem', color: impS.color, bgcolor: impS.bg, border: `1px solid ${impS.border}` }} />
                  <Typography sx={{ fontSize: '0.62rem', color: C.textDim }}>{r.roadmap?.length || 0}阶段</Typography>
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* 右侧详情 */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {!rel ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
            <Typography sx={{ color: C.textDim, fontSize: '0.9rem' }}>请从左侧选择一组关系查看详情</Typography>
          </Box>
        ) : (
          <Box>
            <Box sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${C.border}`, bgcolor: 'rgba(0,212,184,0.03)' }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <FavoriteIcon sx={{ fontSize: 26, color: C.accent }} />
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', flex: 1, wordBreak: 'break-word' }}>
                  {rel.pair?.length >= 2 ? `${rel.pair[0]} — ${rel.pair[1]}` : rel.id}
                </Typography>
                <Chip label={`${imp.label}关系`} size="small" sx={{ height: 22, fontSize: '0.7rem', color: imp.color, bgcolor: imp.bg, border: `1px solid ${imp.border}` }} />
              </Stack>
              {rel.relationshipType && (
                <Typography sx={{ pl: 5, color: C.textSec, fontSize: '0.82rem', mt: 0.5 }}>{rel.relationshipType}</Typography>
              )}
            </Box>

            <Box sx={{ borderBottom: `1px solid ${C.border}` }}>
              <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} variant="fullWidth"
                sx={{ '& .MuiTab-root': { color: C.textDim, minHeight: 48, fontSize: '0.78rem', '&.Mui-selected': { color: C.accent } }, '& .MuiTabs-indicator': { bgcolor: C.accent } }}>
                <Tab label="路线图" icon={<TimelineIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                <Tab label="转折点" icon={<TrendingUpIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                <Tab label="冲突" icon={<SwapHorizIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                <Tab label="秘密" icon={<LockIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              </Tabs>
            </Box>

            <Box sx={{ p: 3 }}>
              {detailTab === 0 && <PCRoadmap rel={rel} />}
              {detailTab === 1 && <PCTurningPoints rel={rel} />}
              {detailTab === 2 && <PCConflicts rel={rel} />}
              {detailTab === 3 && <PCSecrets rel={rel} />}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

/* ===================== PC 路线图 ===================== */
const PCRoadmap = ({ rel }) => {
  const roadmap = rel.roadmap || [];
  if (!roadmap.length) return <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim }}>暂无发展路线图</Typography></Box>;
  return (
    <Stack spacing={1.5}>
      {roadmap.map((stage, idx) => (
        <Box key={idx} sx={{ borderLeft: `3px solid ${C.accent}`, pl: 2, py: 0.5 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: C.accent, color: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.68rem', fontWeight: 700, flexShrink: 0 }}>{idx + 1}</Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', flex: 1 }}>{stage.stage}</Typography>
            {stage.chapterRange?.length === 2 && (
              <Chip label={`${stage.chapterRange[0]}-${stage.chapterRange[1]}章`} size="small" sx={{ height: 20, fontSize: '0.62rem', bgcolor: C.accentDim, color: C.accent }} />
            )}
          </Stack>
          {stage.trustLevel && (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1, pl: 4 }}>
              <TrustBar label="起始" value={stage.trustLevel.start} />
              <Typography sx={{ color: C.textDim }}>→</Typography>
              <TrustBar label="结束" value={stage.trustLevel.end} />
            </Stack>
          )}
          {stage.emotionalTone && (
            <Typography sx={{ pl: 4, color: C.textSec, fontSize: '0.8rem', mb: 0.75 }}>情感基调：<Box component="span" sx={{ color: C.text }}>{stage.emotionalTone}</Box></Typography>
          )}
          {stage.keyMilestones?.length > 0 && (
            <Stack spacing={0.5} sx={{ pl: 4 }}>
              {stage.keyMilestones.map((m, mi) => (
                <Box key={mi} sx={{ pl: 1.5, borderLeft: `2px solid ${C.accentBor}` }}>
                  <Typography sx={{ fontSize: '0.8rem' }}>
                    <Box component="span" sx={{ color: C.accent, fontWeight: 700, mr: 0.5 }}>第{m.chapter}章</Box>
                    <Box component="span" sx={{ color: C.textSec }}>{m.event}</Box>
                  </Typography>
                  {m.impact && <Typography sx={{ color: C.textDim, fontSize: '0.72rem', pl: 1.5 }}>→ {m.impact}</Typography>}
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      ))}
    </Stack>
  );
};

const TrustBar = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Typography sx={{ color: C.textDim, fontSize: '0.7rem' }}>{label}</Typography>
    <Box sx={{ width: 60, height: 5, bgcolor: C.accentDim, borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ height: '100%', width: `${(value || 0) * 10}%`, bgcolor: getTrustColor(value || 0), borderRadius: 1 }} />
    </Box>
    <Typography sx={{ fontWeight: 700, color: getTrustColor(value || 0), fontSize: '0.75rem' }}>{value || 0}/10</Typography>
  </Box>
);

/* ===================== PC 转折点 ===================== */
const PCTurningPoints = ({ rel }) => {
  const tps = rel.turningPoints || [];
  if (!tps.length) return <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim }}>暂无关键转折数据</Typography></Box>;
  return (
    <Grid container spacing={1.5}>
      {tps.map((tp, idx) => (
        <Grid item xs={12} sm={6} key={idx}>
          <Box sx={{ borderLeft: `3px solid ${C.amber}`, pl: 1.5, py: 0.5 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.5 }}>
              <Chip label={`第${tp.chapter}章`} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: C.amberDim, color: C.amber, border: `1px solid ${C.amberBor}`, flexShrink: 0 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', lineHeight: 1.4 }}>{tp.event}</Typography>
            </Stack>
            {tp.impact && (
              <Typography sx={{ color: C.textSec, fontSize: '0.82rem', pl: 5 }}>
                <Box component="span" sx={{ color: C.amber, fontWeight: 600, mr: 0.5 }}>影响</Box>{tp.impact}
              </Typography>
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

/* ===================== PC 冲突 ===================== */
const PCConflicts = ({ rel }) => {
  const conflicts = rel.conflicts || [];
  if (!conflicts.length) return <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim }}>暂无冲突数据</Typography></Box>;
  return (
    <Stack spacing={1.5}>
      {conflicts.map((c, idx) => (
        <Box key={idx} sx={{ borderLeft: `3px solid ${C.red}`, pl: 1.5, py: 0.5 }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.5 }}>
            <Chip label={`第${c.chapter}章`} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: C.redDim, color: C.red, border: `1px solid ${C.redBor}`, flexShrink: 0 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: C.red, mb: 0.25 }}>{c.type}</Typography>
              <Typography sx={{ color: C.textSec, fontSize: '0.82rem' }}>{c.description}</Typography>
            </Box>
          </Stack>
          {c.resolution && (
            <Box sx={{ pl: 5, pt: 0.75, borderTop: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#4ade80', flexShrink: 0 }}>解决</Typography>
              <Typography sx={{ color: C.textSec, fontSize: '0.75rem', flex: 1 }}>{c.resolution.method}</Typography>
              {c.resolution.chapter && (
                <Chip label={`${c.resolution.chapter}章`} size="small" sx={{ height: 18, fontSize: '0.62rem', bgcolor: 'rgba(74,222,128,0.1)', color: '#4ade80' }} />
              )}
            </Box>
          )}
        </Box>
      ))}
    </Stack>
  );
};

/* ===================== PC 秘密 ===================== */
const PCSecrets = ({ rel }) => {
  const secrets = rel.secretRevealSchedule || [];
  if (!secrets.length) return <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: C.textDim }}>暂无秘密透露数据</Typography></Box>;
  return (
    <Grid container spacing={1.5}>
      {secrets.map((s, idx) => (
        <Grid item xs={12} sm={6} key={idx}>
          <Box sx={{ borderLeft: `3px solid ${C.purple}`, pl: 1.5, py: 0.5 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Chip label={`第${s.chapter}章`} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: C.purpleDim, color: C.purple, border: `1px solid ${C.purpleBor}`, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 0.25 }}>{s.secret}</Typography>
                {s.detail && <Typography sx={{ color: C.textSec, fontSize: '0.82rem' }}>{s.detail}</Typography>}
              </Box>
            </Stack>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

export default RelationshipPlanViewer;
